import { useState } from "react";
import type {
  BuyerResearchRunSummaryDto,
  BuyerResearchSourceLogEntryDto,
} from "@/api/buyerResearch";
import { revertRefusalBlocker } from "@/api/buyerResearch";
import { extractErrorMessage } from "@/api/client";
import { Button, Timestamp } from "@/design-system/components/ui";
import { useUserTimeZone } from "@/utils/datetime";
import { researchCopy } from "./researchCopy";
import { formatShare } from "./researchFormat";
import { useRevertQueryChanges } from "./useBuyerResearch";

/**
 * Area C — what the adaptive-search probe changed about this run's query, and
 * the control to put it back.
 *
 * <p>🔴 <b>A REVERT APPLIES TO THE BUYER'S NEXT RUN AND UN-BUYS NOTHING.</b>
 * The records this run spent are spent, the candidates below stay exactly where
 * they are, and the filter it searched stays on the record as the account of
 * what the money bought. That is stated BESIDE THE BUTTON, before the press —
 * a control called "revert" that explains itself only in a toast afterwards has
 * already let somebody expect a refund.</p>
 *
 * <p>🔴 <b>THE CLIENT DECIDES NOTHING</b>, the rule its sibling
 * {@link ResearchPullControl} states: whether a revert is available, and why
 * not when it is not, arrive on <c>run.queryRevert</c> from the one expression
 * the endpoint's own pre-flight runs. A second predicate here would be free to
 * offer a revert the server refuses or hide one it would have accepted.</p>
 *
 * <p><b>ABSENT rather than disabled when there is nothing to revert.</b> Most
 * runs converge on nothing at all — a brief that already lands inside the
 * target band is the good case — and a permanently greyed control on every one
 * of them is noise a reader learns to stop seeing. Same reasoning as RC14's on
 * the pull control.</p>
 *
 * <p>The transcript itself renders whatever the run recorded, including on a
 * run nobody can revert: <b>what the probe did is worth reading even when it
 * changed nothing</b>, because "this search bought exactly what the brief
 * describes" is the answer to "why these listings?" just as much as a
 * narrowing is.</p>
 */
export function ResearchQueryTranscript({
  buyerLeadId,
  run,
  sourceLog,
  canEdit,
}: {
  buyerLeadId: string;
  run: BuyerResearchRunSummaryDto;
  /** The run's whole source log. At most one entry carries a probe. */
  sourceLog: BuyerResearchSourceLogEntryDto[];
  canEdit: boolean;
}) {
  const copy = researchCopy.transcript;
  const tz = useUserTimeZone();
  const revert = useRevertQueryChanges(buyerLeadId);
  const [error, setError] = useState<string | null>(null);
  const [stepsOpen, setStepsOpen] = useState(false);

  // 🔴 The FIRST entry that probed, not the Clef key. Which source can narrow
  // its own query is the source's business and this component's business is the
  // record it left; keying on "clef" would make a second paged vendor invisible
  // here while the server was already writing its transcript.
  const probed = sourceLog.find((e) => e.probeOutcome != null);
  const state = run.queryRevert;

  // Nothing probed and nothing to say about it. A run from before adaptive
  // search, or one on a source that cannot narrow.
  if (probed == null) return null;

  const narrowed =
    probed.matchesBeforeProbe != null &&
    probed.matchesAfterProbe != null &&
    probed.matchesBeforeProbe !== probed.matchesAfterProbe;

  const steps = probed.probeSteps ?? [];

  // No `border-t` on this section, and none on any sibling. The selected run
  // has SIX siblings — brief, query transcript, sources, Ask Phoenix, results,
  // pull — and THREE drew a rule above themselves: the transcript, Ask Phoenix
  // and this one. So the rule marked no boundary a reader could name; it marked
  // which components happened to have been written with one. 🔴 And the three
  // do not even share a shape: two of them render their own heading and the
  // pull control renders none, so the rule was not tracking "a titled section
  // starts here" either. §17.5 calls that a structural signal corresponding to
  // no structure; §17.2's heading ladder carries the division and the parent's
  // `gap-space-4` carries the spacing.
  //
  // ⚠️ The count is SIX, not the five the §17.4 note in `ResearchTab` cites:
  // that note is counting sections that render a HEADING, which the pull
  // control does not. Re-derived rather than copied.
  return (
    <section
      className="grid gap-space-2"
      data-testid="research-query-transcript"
      data-probe-outcome={probed.probeOutcome}
    >
      <h3 className="m-0 text-caption uppercase tracking-wide text-gray-mid">{copy.heading}</h3>

      <p className="m-0 text-caption text-gray-mid break-words" data-testid="research-transcript-headline">
        {narrowed
          ? copy.narrowed(probed.matchesBeforeProbe!, probed.matchesAfterProbe!)
          : copy.unchanged}
      </p>

      {/* Why the run did not narrow, when the server said why. Without it
          "skipped" has three causes and one appearance, and one of them is a
          specialist's own instruction. */}
      {probed.probeSkippedReason && (
        <p
          className="m-0 text-caption text-gray-mid break-words"
          data-testid="research-transcript-skipped-reason"
        >
          {probed.probeSkippedReason}
        </p>
      )}

      {probed.probeVariantsTried > 0 && (
        <p className="m-0 text-caption text-gray-mid" data-testid="research-transcript-variants">
          {copy.variants(probed.probeVariantsTried)}
        </p>
      )}

      {/* 🔴 BOTH RATES OR NEITHER. The fit share is a share of the listings that
          PUBLISH a multiple, so alone it overstates the market by exactly the
          non-publishing remainder — which is the misreading its own server-side
          doc exists to prevent. */}
      {probed.fitDensity != null && probed.publicationRate != null && (
        <p className="m-0 text-caption text-gray-mid break-words" data-testid="research-transcript-density">
          {copy.density(formatShare(probed.fitDensity), formatShare(probed.publicationRate))}
        </p>
      )}

      {/* Ruling 7 — the by-product of the three free counts above, and the only
          measurement Phoenix takes of the assumption its whole filter policy
          rests on: D5 withholds every financial bound from the wire on the
          INFERENCE that the vendor drops listings publishing no figure, and this
          says whether it does.

          🔴 TRI-STATE, AND THE NULL ARM IS THE ONE THAT MATTERS. `undefined`
          (a client built before the member existed) and `null` (the probe never
          reached the density step) both render NOTHING. Rendering either as
          `false` would turn an absent measurement into a confirmation of the
          very premise it exists to test — which is the one reading that makes
          the line worse than useless.

          ⚠️ One probe on one query, taken with a tolerance that leans toward
          "they passed", is EVIDENCE and not a ruling. The sentence says what was
          seen on this search and stops; the value is in the population it
          accumulates. */}
      {typeof probed.nullsPassAnUpperBound === "boolean" && (
        <p
          className="m-0 text-caption text-gray-mid break-words"
          data-testid="research-transcript-null-bound"
          data-passed={probed.nullsPassAnUpperBound}
        >
          {copy.nullBound(probed.nullsPassAnUpperBound)}
        </p>
      )}

      {steps.length > 0 && (
        <div className="grid gap-space-1">
          <Button
            type="button"
            variant="text"
            size="sm"
            aria-expanded={stepsOpen}
            onClick={() => setStepsOpen((open) => !open)}
            data-testid="research-transcript-steps-toggle"
          >
            {copy.stepsHeading} ({steps.length})
          </Button>
          {stepsOpen && (
            <ul className="m-0 p-0 list-none grid gap-space-1" data-testid="research-transcript-steps">
              {steps.map((step, i) => (
                <li
                  // Position, not the change text: two attempts can propose the
                  // same change against different baselines, and the transcript
                  // is an ordered record rather than a set.
                  key={`${step.lever}:${i}`}
                  className="flex flex-wrap items-baseline gap-space-2 text-caption text-gray-mid"
                  data-testid="research-transcript-step"
                  data-accepted={step.accepted}
                >
                  <span className="text-onyx break-words">{step.change}</span>
                  <span className="tabular-nums">
                    {step.matches != null ? step.matches.toLocaleString() : copy.stepNotCounted}
                  </span>
                  <span className="break-words">
                    {step.accepted ? copy.stepAccepted : copy.stepRejected(step.rejectedBecause)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {error && (
        <p className="m-0 text-caption text-status-error" role="alert" data-testid="research-transcript-error">
          {error}
        </p>
      )}

      {state?.revertedAtUtc != null ? (
        <p className="m-0 text-caption text-gray-mid break-words" data-testid="research-transcript-reverted">
          <Timestamp value={state.revertedAtUtc} tz={tz} /> {copy.revertedBy(state.revertedByName)}
        </p>
      ) : state?.canRevert && canEdit ? (
        <div className="grid gap-space-1" data-testid="research-transcript-revert">
          {/* 🔴 BEFORE THE PRESS. This is the sentence that stops the control
              reading as a refund. */}
          <p className="m-0 text-caption text-gray-mid break-words">{copy.consequence}</p>
          <div>
            <Button
              size="sm"
              variant="secondary"
              loading={revert.isPending}
              onClick={() => {
                setError(null);
                revert.mutate(run.id, {
                  onError: (e: unknown) => setError(refusalMessage(e)),
                });
              }}
              data-testid="research-transcript-revert-action"
            >
              {revert.isPending ? copy.actionPending : copy.action}
            </Button>
          </div>
        </div>
      ) : null}
    </section>
  );
}

/**
 * The sentence for a blocker code.
 *
 * <p>A code this client has not learned yet falls back to a readable sentence
 * rather than the slug, and never to nothing — a refusal with no explanation is
 * worse than one whose explanation is general.</p>
 */
function blockerSentence(blocker: string | null): string {
  const copy = researchCopy.transcript;
  return (blocker != null && copy.blocked[blocker]) || copy.blockedFallback;
}

/**
 * 🔴 A refused revert is a 400 carrying a blocker CODE in `details.code` — read
 * it with `revertRefusalBlocker`, never from the message, so the surface
 * renders its own copy and a reworded server message cannot silently stop
 * matching.
 *
 * <p>Anything else — a network failure, a 404, a 500 — is not a blocker and
 * falls through to the ordinary message, which is the honest thing to show for
 * a failure that is not a decision.</p>
 */
function refusalMessage(e: unknown): string {
  const blocker = revertRefusalBlocker(e);
  return blocker != null ? blockerSentence(blocker) : extractErrorMessage(e);
}
